import { useIsAuthorized } from '@modules/common/components/CanAccess/hooks';
import { AddPredictions } from '@modules/feedback/components/AddPredictions';
import {
  FeedbackCategory,
  FeedbackCategoryProps,
} from '@modules/feedback/components/FeedbackCard/FeedbackCategories/FeedbackCategory';
import { getReasonSuggestionsFromSuggestedReasons } from '@modules/feedback/components/FeedbackDetails/utils';
import { FeedbackPredictionActions } from '@modules/feedback/components/FeedbackPredictionActions';
import {
  useGetFeedbackReasonLabels,
  useGetFeedbackTrackedKeywordLabels,
  useIsFeedbackOnFeedbackEnabled,
} from '@modules/feedback/hooks';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { FeedbackCategoriesLabels } from '@modules/feedback/types';
import { trackFeedbackOnFeedbackV2Events } from '@modules/feedback/utils';
import { useIsFeedbackRedesignEnabled } from '@modules/feedbackV2/common/hooks';
import { Box, Paper, Popper, Stack, Typography } from '@mui/material';
import {
  PermissionOperation,
  PermissionResource,
  SuggestedReason,
  useGetReasonSuggestionQuery,
} from 'generated/graphql';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Feedback,
  FeedbackContentType,
  FeedbackLabel,
} from 'v2/core/entities/feedback';
import { useGetUser } from 'v2/core/services/user';
import { FeedbackMetaData } from 'v2/store/useViewFeedbackStore';
import {
  getLabelsToCategoryPairArray,
  getSelectedCategoryLabels,
} from './utils';

interface FeedbackCategoriesProps {
  feedback: Feedback;
  feedbackLabels: FeedbackCategoriesLabels;
  setSelectedLabels?: (selectedLabels: FeedbackLabel[]) => void;
  disabledLabelIDs?: string[];
  selectedLabels?: FeedbackLabel[];
  shouldSortBySelection?: boolean;
  isDetailedView?: boolean;
  isEditPredictionsMode?: boolean;
  disableLabelSelection?: boolean;
  onFeedbackCardLabelClick?: (selectedLabels: FeedbackLabel[]) => void;
  selectedFeedbackMetadata?: FeedbackMetaData[];
  isFeedbackDetailsDrawerVisible?: boolean;
}

export const FeedbackCategories = ({
  feedback,
  feedbackLabels,
  selectedLabels = [],
  disabledLabelIDs,
  setSelectedLabels,
  onFeedbackCardLabelClick,
  shouldSortBySelection = false,
  disableLabelSelection,
  isDetailedView = false,
  isEditPredictionsMode = false,
  selectedFeedbackMetadata,
  isFeedbackDetailsDrawerVisible = false,
}: FeedbackCategoriesProps) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const canAddPredictions = useIsAuthorized({
    operation: PermissionOperation.Write,
    resource: PermissionResource.Prediction,
  });

  const { isFeedbackOnFeedbackEnabled } = useIsFeedbackOnFeedbackEnabled();
  const isFeedbackRedesignEnabled = useIsFeedbackRedesignEnabled();

  const editedRecords = useFeedbackOnFeedbackStore(
    ({ editedRecords }) => editedRecords,
  );

  const [isAddPredictionsVisible, setIsAddPredictionsVisible] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const isAddingPredictionsDisabled =
    feedback.content.type === FeedbackContentType.Audio || !canAddPredictions;

  const isAddPredictionsPopperOpen = isAddPredictionsVisible
    ? Boolean(anchorEl)
    : false;

  const { data, loading } = useGetReasonSuggestionQuery({
    variables: {
      request: {
        recordID: feedback.id,
        tenantID,
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: isAddingPredictionsDisabled || !isAddPredictionsPopperOpen,
  });

  const areReasonSuggestionsLoading = data ? false : loading;

  const suggestedReasons = (data?.getReasonSuggestion?.suggestedReasons ??
    []) as SuggestedReason[];

  const reasonSuggestions = getReasonSuggestionsFromSuggestedReasons(
    suggestedReasons,
    editedRecords,
  );

  const reasonLabels = useGetFeedbackReasonLabels({
    labels: feedbackLabels.reasonLabels,
    feedbackID: feedback.id,
    selectedLabels,
    shouldSortBySelection,
  });

  const trackedKeywordLabels = useGetFeedbackTrackedKeywordLabels({
    labels: feedbackLabels.entityLabels,
    feedbackID: feedback.id,
    selectedLabels,
    shouldSortBySelection,
  });

  const predictedCategories = getLabelsToCategoryPairArray({
    labels: [...reasonLabels, ...trackedKeywordLabels],
  });

  useEffect(() => {
    if (isFeedbackDetailsDrawerVisible) {
      setIsAddPredictionsVisible(false);
      setAnchorEl(null);
    }
  }, [isFeedbackDetailsDrawerVisible]);

  const renderFeedbackCategories = (props?: Partial<FeedbackCategoryProps>) => {
    if (predictedCategories.length === 0) {
      return (
        <Typography
          variant={isFeedbackRedesignEnabled ? 'body2' : 'caption'}
          textAlign="left"
          color="text.secondary"
          pt={isFeedbackRedesignEnabled ? 2 : 0}
          pl={isFeedbackRedesignEnabled && isDetailedView ? 2 : 0}
        >
          {isFeedbackRedesignEnabled && isDetailedView ? (
            <>
              No predictions found. You can add predictions to this feedback by
              clicking on{' '}
              <Box component="span" fontWeight="bold">
                + Add Predictions
              </Box>
            </>
          ) : (
            'No predictions found.'
          )}
        </Typography>
      );
    }

    return predictedCategories.map(([category, labels]) => {
      const categorySpecificLabels = getSelectedCategoryLabels({
        selectedLabels,
        category,
        selectedFeedbackMetadata,
      });

      return (
        <FeedbackCategory
          selectedLabels={categorySpecificLabels}
          setSelectedLabels={setSelectedLabels}
          feedback={feedback}
          feedbackLabels={labels}
          isDetailedView={isDetailedView}
          category={category}
          isEditPredictionsMode={isEditPredictionsMode}
          {...props}
        />
      );
    });
  };

  const onClose = useCallback(() => {
    setIsAddPredictionsVisible(false);
    trackFeedbackOnFeedbackV2Events({
      eventName: 'Add Predictions View Closed',
      feedbackID: feedback.id,
      toggleLocation: 'Feedback List View',
    });
  }, [feedback.id]);

  // This is to fix the flickering of popover when opened for the first time
  useEffect(() => {
    setAnchorEl(anchorRef.current);
  }, []);

  const onOpenAddPredictions = useCallback(() => {
    setIsAddPredictionsVisible(true);
    setAnchorEl(anchorRef.current);
    trackFeedbackOnFeedbackV2Events({
      eventName: 'Add Predictions View Opened',
      feedbackID: feedback.id,
      toggleLocation: 'Feedback List View',
    });
  }, [feedback.id]);

  if (isDetailedView) {
    return (
      <Stack mt={2} spacing={2}>
        {renderFeedbackCategories()}
      </Stack>
    );
  }

  const shouldShowFeedbackPredictionActions =
    reasonSuggestions && areReasonSuggestionsLoading !== undefined;

  return (
    <Stack spacing={1}>
      <Stack spacing={1} direction="row" alignItems="center">
        <Typography
          variant={isFeedbackOnFeedbackEnabled ? 'body1' : 'body2'}
          color="text.secondary"
        >
          Predictions
        </Typography>
        {shouldShowFeedbackPredictionActions && (
          <Box ref={anchorRef}>
            <FeedbackPredictionActions
              feedback={feedback}
              reasonSuggestions={reasonSuggestions}
              areSuggestionsLoading={areReasonSuggestionsLoading}
              isAddPredictionsVisible={isAddPredictionsVisible}
              onOpen={onOpenAddPredictions}
              onClose={onClose}
              isDetailedView={false}
            />
            <Popper
              open={isAddPredictionsPopperOpen}
              anchorEl={anchorEl}
              placement="bottom-start"
              disablePortal
              sx={{
                zIndex: (theme) => theme.zIndex.modal,
                width: 450,
              }}
              keepMounted
            >
              <Paper
                elevation={8}
                sx={{
                  mt: 1,
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                }}
              >
                <AddPredictions
                  onClose={onClose}
                  isOpen={isAddPredictionsVisible}
                  feedback={feedback}
                  reasonSuggestions={reasonSuggestions}
                  areReasonSuggestionsLoading={areReasonSuggestionsLoading}
                  isDetailView={false}
                />
              </Paper>
            </Popper>
          </Box>
        )}
      </Stack>
      <Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'max-content auto',
            columnGap: 2,
            rowGap: 1,
          }}
        >
          {renderFeedbackCategories({
            onFeedbackCardLabelClick,
            shouldSortBySelection,
            isEditPredictionsMode: false,
            disabledLabelIDs,
            disableLabelSelection,
          })}
        </Box>
      </Box>
    </Stack>
  );
};
